import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, take, tap } from 'rxjs/operators';

import { ApiHttp } from '@core/api/api-http';
import { OpportunityApiHttp } from '@core/api/opportunity-api-http';

export interface BrokerCapabilities {
  factFind: boolean;
  overseasAddress: boolean;
}

const FAIL_CLOSED: BrokerCapabilities = {
  factFind: false,
  overseasAddress: false
};

@Injectable({
  providedIn: 'root'
})
export class BrokerCapabilitiesService {
  private readonly loaded$ = new BehaviorSubject(false);
  private cache: BrokerCapabilities = FAIL_CLOSED;
  private loadRequest$?: Observable<BrokerCapabilities>;

  constructor(private readonly http: OpportunityApiHttp) {}

  /** Fetch capabilities once; subsequent calls are no-ops. */
  load(): void {
    if (this.loadRequest$) {
      return;
    }

    this.loadRequest$ = this.http
      .get<BrokerCapabilities>(
        'broker/capabilities',
        ApiHttp.getStandardOptions()
      )
      .pipe(
        catchError(() => of(FAIL_CLOSED)),
        tap((capabilities) => {
          this.cache = {
            factFind: !!capabilities.factFind,
            overseasAddress: !!capabilities.overseasAddress
          };
          this.loaded$.next(true);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.loadRequest$.subscribe();
  }

  /** Emits after the first load attempt completes (success or fail-closed). */
  whenReady(): Observable<BrokerCapabilities> {
    return this.loaded$.pipe(
      filter((loaded) => loaded),
      take(1),
      map(() => this.cache)
    );
  }

  /** Current cached value; false until the first load completes. */
  getCached(): BrokerCapabilities {
    return this.cache;
  }

  hasLoaded(): boolean {
    return this.loaded$.value;
  }
}
